import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Star, MapPin, Building2, Home, Tag, Layers, Navigation, ExternalLink, ShoppingBag, Globe, Instagram, MessageCircle, Store, ChevronLeft, ChevronRight, Loader2, Package, Phone } from 'lucide-react';
import { getProjectFull, getImageUrl } from '../lib/api';
import '../styles/project-page.css';

const CATEGORY_IMAGES = {
  'الصناعة والتصنيع': 'https://images.unsplash.com/photo-1775150031281-32625b350fba?w=800&h=400&fit=crop&q=80',
  'الزراعة والثروة الحيوانية': 'https://images.unsplash.com/photo-1729041221905-0519efecaa92?w=800&h=400&fit=crop&q=80',
  'التجارة والتجزئة': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop&q=80',
  'السياحة والضيافة': 'https://images.unsplash.com/photo-1748741426070-f11ac876e7dd?w=800&h=400&fit=crop&q=80',
  'التقنية والاتصالات': 'https://images.unsplash.com/photo-1693880269221-c064425aa8a8?w=800&h=400&fit=crop&q=80',
  'الخدمات المالية': 'https://images.unsplash.com/photo-1773892849970-64e651f5db9b?w=800&h=400&fit=crop&q=80',
  'الصحة والرعاية الطبية': 'https://images.unsplash.com/photo-1769147555720-71fc71bfc216?w=800&h=400&fit=crop&q=80',
  'التعليم والتدريب': 'https://images.unsplash.com/photo-1590579491624-f98f36d4c763?w=800&h=400&fit=crop&q=80',
};
const DEFAULT_IMG = 'https://images.unsplash.com/photo-1772736517917-1a22354adfe6?w=800&h=400&fit=crop&q=80';

const CHANNEL_ICONS = {
  store: Store, website: Globe, instagram: Instagram,
  whatsapp: MessageCircle, phone: Phone, marketplace: ShoppingBag,
};
const CHANNEL_LABELS = {
  store: 'متجر', website: 'موقع إلكتروني', instagram: 'انستجرام',
  whatsapp: 'واتساب', phone: 'هاتف', marketplace: 'سوق إلكتروني',
};

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    getProjectFull(id)
      .then(res => setProject(res.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return (
    <div className="pp-loading"><Loader2 className="pp-spinner" size={32} /></div>
  );
  if (!project) return null;

  const catNameAr = project.category?.name_ar || '';
  const subcatNameAr = project.subcategory?.name_ar || '';
  const regionName = project.region?.name_ar?.replace(/^(منطقة |المنطقة )/, '') || '';
  const cityName = project.city?.name_ar || '';
  const districtName = project.district?.name_ar || '';

  const hasUploadedImages = project.images?.length > 0;
  const galleryImages = hasUploadedImages
    ? project.images.map(img => getImageUrl(img))
    : [CATEGORY_IMAGES[catNameAr] || DEFAULT_IMG];

  const products = project.products || [];
  const salesChannels = project.sales_channels || [];
  const productImages = project.product_images || [];

  return (
    <div className="pp-root" data-testid="project-page">
      {/* Lightbox */}
      {lightbox !== null && (
        <div className="pp-lightbox" onClick={() => setLightbox(null)} data-testid="lightbox">
          <img src={lightbox} alt="" className="pp-lightbox-img" />
        </div>
      )}

      {/* Back button */}
      <button className="pp-back" data-testid="project-page-back" onClick={() => navigate('/')}>
        <ArrowRight size={18} />
        <span>العودة للخريطة</span>
      </button>

      {/* Hero gallery */}
      <div className="pp-hero" data-testid="project-hero">
        <img src={galleryImages[galleryIdx]} alt={project.project_name_ar} className="pp-hero-img" onClick={() => setLightbox(galleryImages[galleryIdx])} />
        <div className="pp-hero-overlay" />
        {galleryImages.length > 1 && (
          <>
            <button className="pp-hero-nav pp-hero-prev" onClick={() => setGalleryIdx(i => (i - 1 + galleryImages.length) % galleryImages.length)}>
              <ChevronRight size={22} />
            </button>
            <button className="pp-hero-nav pp-hero-next" onClick={() => setGalleryIdx(i => (i + 1) % galleryImages.length)}>
              <ChevronLeft size={22} />
            </button>
            <div className="pp-hero-dots">
              {galleryImages.map((_, i) => (
                <span key={i} className={`pp-hero-dot ${i === galleryIdx ? 'active' : ''}`} onClick={() => setGalleryIdx(i)} />
              ))}
            </div>
          </>
        )}
        {/* Thumbnail strip */}
        {galleryImages.length > 1 && (
          <div className="pp-thumbs">
            {galleryImages.map((img, i) => (
              <img key={i} src={img} alt="" className={`pp-thumb ${i === galleryIdx ? 'active' : ''}`} onClick={() => setGalleryIdx(i)} />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="pp-content">
        {/* Header */}
        <div className="pp-header">
          <div className="pp-header-top">
            <h1 className="pp-title" data-testid="project-page-title">{project.project_name_ar}</h1>
            {project.is_featured && (
              <span className="pp-featured-badge" data-testid="project-page-featured">
                <Star size={14} fill="#D6A84F" color="#D6A84F" />
                <span>مشروع مميز</span>
              </span>
            )}
          </div>
          {project.short_description_ar && (
            <p className="pp-short-desc" data-testid="project-page-short-desc">{project.short_description_ar}</p>
          )}
        </div>

        {/* Info cards */}
        <div className="pp-info-grid">
          <div className="pp-info-card">
            <Layers size={16} className="pp-info-icon" style={{ color: '#2FAE8F' }} />
            <div>
              <span className="pp-info-label">الفئة</span>
              <span className="pp-info-val">{catNameAr}</span>
            </div>
          </div>
          {subcatNameAr && (
            <div className="pp-info-card">
              <Tag size={16} className="pp-info-icon" style={{ color: '#5DADE2' }} />
              <div>
                <span className="pp-info-label">الفئة الفرعية</span>
                <span className="pp-info-val">{subcatNameAr}</span>
              </div>
            </div>
          )}
          <div className="pp-info-card">
            <Building2 size={16} className="pp-info-icon" style={{ color: '#BB8FCE' }} />
            <div>
              <span className="pp-info-label">المنطقة</span>
              <span className="pp-info-val">{regionName}</span>
            </div>
          </div>
          <div className="pp-info-card">
            <MapPin size={16} className="pp-info-icon" style={{ color: '#F1948A' }} />
            <div>
              <span className="pp-info-label">المدينة</span>
              <span className="pp-info-val">{cityName}</span>
            </div>
          </div>
          {districtName && (
            <div className="pp-info-card">
              <Home size={16} className="pp-info-icon" style={{ color: '#D6A84F' }} />
              <div>
                <span className="pp-info-label">الحي</span>
                <span className="pp-info-val">{districtName}</span>
              </div>
            </div>
          )}
        </div>

        {/* Navigate */}
        {project.latitude && project.longitude && (
          <a href={`https://www.google.com/maps/dir/?api=1&destination=${project.latitude},${project.longitude}`}
            target="_blank" rel="noreferrer" className="pp-nav-btn" data-testid="project-page-navigate">
            <Navigation size={16} />
            <span>الذهاب للموقع</span>
            <ExternalLink size={12} style={{ opacity: 0.5 }} />
          </a>
        )}

        {/* Description */}
        {project.description_ar && (
          <div className="pp-section" data-testid="project-page-description">
            <h2 className="pp-section-title">وصف المشروع</h2>
            <p className="pp-description">{project.description_ar}</p>
          </div>
        )}

        {/* Tags */}
        {project.tags?.length > 0 && (
          <div className="pp-tags" data-testid="project-page-tags">
            {project.tags.map((tag, i) => <span key={i} className="pp-tag">{tag}</span>)}
          </div>
        )}

        {/* Products */}
        {products.length > 0 && (
          <div className="pp-section" data-testid="project-page-products">
            <h2 className="pp-section-title"><Package size={18} /> المنتجات</h2>
            <div className="pp-products-grid">
              {products.map(prod => (
                <div key={prod.id} className="pp-product-card">
                  {prod.image && (
                    <img src={getImageUrl(prod.image)} alt={prod.name_ar} className="pp-product-img" onClick={() => setLightbox(getImageUrl(prod.image))} />
                  )}
                  <div className="pp-product-body">
                    <h4 className="pp-product-name">{prod.name_ar}</h4>
                    {prod.description_ar && <p className="pp-product-desc">{prod.description_ar}</p>}
                    {prod.price && <span className="pp-product-price">{prod.price} ر.س</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sales Channels */}
        {salesChannels.length > 0 && (
          <div className="pp-section" data-testid="project-page-sales-channels">
            <h2 className="pp-section-title"><Store size={18} /> قنوات البيع</h2>
            <div className="pp-channels-grid">
              {salesChannels.map(ch => {
                const Icon = CHANNEL_ICONS[ch.channel_type] || Globe;
                return (
                  <a key={ch.id} href={ch.url || '#'} target="_blank" rel="noreferrer" className="pp-channel-card">
                    <Icon size={20} className="pp-channel-icon" />
                    <span className="pp-channel-label">{ch.label || CHANNEL_LABELS[ch.channel_type] || ch.channel_type}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Product Images Gallery */}
        {productImages.length > 0 && (
          <div className="pp-section" data-testid="project-page-product-images">
            <h2 className="pp-section-title">صور المنتجات</h2>
            <div className="pp-pimg-grid">
              {productImages.map(pi => (
                <div key={pi.id} className="pp-pimg-card" onClick={() => setLightbox(getImageUrl(pi.image_path))}>
                  <img src={getImageUrl(pi.thumbnail_path || pi.image_path)} alt={pi.caption || ''} className="pp-pimg-img" />
                  {pi.caption && <span className="pp-pimg-caption">{pi.caption}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
